import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Button } from '@guildeducationinc/recess';
import { AlgoliaFacets } from 'src/clients/Algolia/constants';
import { getRemainingFacetsFromAnswers } from '@src/containers/QuizContainer/answerHelpers';
import { QuizAnalytics, ScreenProps } from '@src/containers/QuizContainer/types';
import { trackEvent } from 'src/services/analytics';
import { RoundIconButton } from 'src/components/Quiz/RoundIconButton';
import { Footer } from 'src/components/Quiz/Footer';
import { useSearchContext } from 'src/components/Providers/SearchProvider';
import { QuizQuestion } from 'src/components/Quiz/QuizQuestion';
import { useQuiz } from '@src/components/Providers/QuizProvider';
import { InterestCategory, interestCategories } from './interestCategories';
import styles from './Interests.module.scss';

const question = 'What are you interested in studying?';

export const Interests: React.FC<ScreenProps> = props => {
	const { filters, index } = useSearchContext();
	const { answers } = useQuiz();
	const { defaultAnswers, nextScreen, previousScreen, updateAnswers } = props;
	const [validInterests, setValidInterests] = useState<InterestCategory[]>([]);

	const selectedInterests = useMemo(() => {
		return answers?.interests ? answers.interests : defaultAnswers?.interests || [];
	}, [answers, defaultAnswers]);

	const analyticsData: QuizAnalytics = useMemo(() => {
		return {
			questionKey: question,
			questionType: 'MultiSelect',
		};
	}, []);

	useEffect(() => {
		const fetchInterests = async () => {
			const { hsGpa, collegeGpa, education, goal, credits } = answers;
			const facets = await getRemainingFacetsFromAnswers(
				{ hsGpa, collegeGpa, education, goal, credits },
				`${AlgoliaFacets.Interests}`,
				filters,
				index
			);
			const availableInterests = interestCategories.filter(category =>
				facets.includes(category.name)
			);
			const lastInterestOption = interestCategories.find(
				category => category.name === "I'm not sure"
			);
			const allValidInterests = [...availableInterests, lastInterestOption];
			const selectedAndValidInterests = allValidInterests
				.filter(({ name }) => selectedInterests.includes(name))
				.map(({ name }) => name);
			setValidInterests([...allValidInterests]);
			updateAnswers({ interests: [...selectedAndValidInterests] }, answers);
		};

		fetchInterests();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters, index]);
	useEffect(() => {
		trackEvent('userProgramQuizQuestionAttempted', analyticsData);
	}, [analyticsData]);

	const handleClick = (name: string) => {
		if (selectedInterests.includes(name)) {
			const interests = selectedInterests.filter(i => i !== name);
			updateAnswers({ interests }, answers);
		} else {
			updateAnswers({ interests: [...selectedInterests, name] }, answers);
		}
	};

	return (
		<>
			<fieldset className={styles.container} data-testid='interests'>
				<legend className={styles.questionTitle} id='interests-label'>
					<QuizQuestion question={question} />
				</legend>
				<div className={styles.questionHelpers}>
					<Typography variant='body1'>Select up to 3 interests.</Typography>
					<Button
						appearance='ghost'
						className={styles.clearSelectionsButton}
						disabled={!selectedInterests.length}
						onClick={() => updateAnswers({ interests: [] }, answers)}
						testid='clear-interests'
					>
						Clear my selections
					</Button>
				</div>
				<div className={styles.iconContainer}>
					{validInterests.map(category => (
						<RoundIconButton
							disabled={!selectedInterests.includes(category.name) && selectedInterests.length >= 3}
							icon={category.icon}
							key={category.name}
							label={category.name}
							onClick={() => handleClick(category.name)}
							popoverText={category.description}
							selected={selectedInterests.includes(category.name)}
						/>
					))}
				</div>
			</fieldset>
			<Footer
				disableNext={!selectedInterests.length}
				onClickNext={() => {
					analyticsData.responseKey = selectedInterests;
					trackEvent('userProgramQuizQuestionCompleted', analyticsData);
					nextScreen();
				}}
				onClickPrevious={previousScreen}
			/>
		</>
	);
};
